import { HttpClient, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { HttpHandlingService } from "./http-handling.service";
import { SERVER_API_URL, SERVER_API_URL_NEW } from "src/app/common/types/app.constants";
import { Observable, catchError, of, switchMap } from "rxjs";
import { Injectable } from "@angular/core";

@Injectable({
    providedIn: 'root'
})
export class ApiDataService {
    /** server api url */
    private SERVER_URI = SERVER_API_URL_NEW;

    constructor(private http: HttpClient, private httpHandlingService: HttpHandlingService) {}

    /**
     * Gets single object from API
     * @param url API endpoint URL
     * @returns object from API
     */
    public get<T>(url: string): Observable<T> {
        const uri = `${this.SERVER_URI}/${url}`;
        return this.http.get<T>(uri, { headers: this.httpHandlingService.authorizationHeaders() }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && this.httpHandlingService.isUserLoggedIn()) {
                    this.httpHandlingService.logout();
                    return of(null);
                }
            })
        );
    }

    /**
     * Gets list of objects from API
     * @param url API endpoint URL
     * @returns list of objects from API
     */
    public getAll<T>(url: string): Observable<T[]> {
        const uri = `${this.SERVER_URI}/${url}`;
        return this.http.get<T[]>(uri, { headers: this.httpHandlingService.authorizationHeaders() }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && this.httpHandlingService.isUserLoggedIn()) {
                    this.httpHandlingService.logout();
                    return of(null);
                }
            })
        );
    }

    /**
     * Sends object to API
     * @param url API endpoint URL
     * @param data data to send
     * @returns API response
     */
    public post<T>(url: string, data: unknown): Observable<T> {
        const uri = `${this.SERVER_URI}/${url}`;
        return this.http.post<T>(uri, data, { headers: this.httpHandlingService.authorizationHeaders() }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && this.httpHandlingService.isUserLoggedIn()) {
                    this.httpHandlingService.logout();
                    return of(null);
                }
            })
        );
    }

    /**
     * Updates object in API
     * @param url API endpoint URL
     * @param data data to send
     * @returns API response
     */
    public put<T>(url: string, data: unknown): Observable<T> {
        const uri = `${this.SERVER_URI}/${url}`;
        return this.http.put<T>(uri, data, { headers: this.httpHandlingService.authorizationHeaders() }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && this.httpHandlingService.isUserLoggedIn()) {
                    this.httpHandlingService.logout();
                    return of(null);
                }
            })
        );
    }

    /**
     * Updates object in API
     * @param url API endpoint URL
     * @param data data to send
     * @returns API response
     */
    public patch<T>(url: string, data: unknown): Observable<T> {
        const uri = `${this.SERVER_URI}/${url}`;
        return this.http.patch<T>(uri, data, { headers: this.httpHandlingService.authorizationHeaders() }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && this.httpHandlingService.isUserLoggedIn()) {
                    this.httpHandlingService.logout();
                    return of(null);
                }
            })
        );
    }

    /**
     * Updates object in API
     * @param url API endpoint URL
     * @param data data to send
     * @returns API response
     */
    public patchEmpty<T>(url: string): Observable<T> {
        const uri = `${this.SERVER_URI}/${url}`;
        return this.http.patch<T>(uri, undefined, { headers: this.httpHandlingService.authorizationHeaders() }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && this.httpHandlingService.isUserLoggedIn()) {
                    this.httpHandlingService.logout();
                    return of(null);
                }
            })
        );
    }

    /**
     * Deletes object in API by parameters
     * @param url API endpoint URL
     * @param data data to send
     * @returns API response
     */
    public delete<T>(url: string, params: unknown): Observable<T> {
        const uri = `${this.SERVER_URI}/${url}/${params}`;
        return this.http.delete<T>(uri, { headers: this.httpHandlingService.authorizationHeaders() }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && this.httpHandlingService.isUserLoggedIn()) {
                    this.httpHandlingService.logout();
                    return of(null);
                }
            })
        );
    }

    /**
   * Gets file from API
   * @param uri URL to get file from
   * @returns File as blob
   */
  public getFile(url: string): Observable<HttpResponse<Blob>> {
    const uri = `${this.SERVER_URI}/${url}`;
    return this.http.get(uri, {
      headers: this.httpHandlingService.downloadFileHeaders(),
      responseType: 'blob',
      observe: 'response',
    });
  }

   /**
   * Downloads file from browser
   * @param url URL to get file from
   */
   public downloadFile(url: string): Observable<boolean> {
    return this.getFile(url).pipe(
      switchMap((response: HttpResponse<Blob>) => {
        /** parse file name from content-disposition header */
        let contentDisposition = response.headers.get('content-disposition');
        let filename = '';
        if (!contentDisposition) {
          filename = 'file';
        } else {
          filename = contentDisposition
            .split(';')[1]
            .split('filename')[1]
            .split('=')[1]
            .trim();
        }

        /** build and download file from browser */
        let dataType = response.body.type;
        let binaryData = [response.body];
        let downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(
          new Blob(binaryData, { type: dataType })
        );
        downloadLink.setAttribute('download', filename);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        return of(true);
      })
    );
  }
}